import React from 'react'
import ReactDOM from 'react-dom/client'
import './index.css'
import { Cut } from './types'
import { Provider } from 'react-redux'
import { store } from './store'
import { getOfficeRuntimeAccessToken } from './utils/common'
import { QueryClientProvider } from 'react-query'
import FormModalProvider from './components/FormModal/FormModalProvider'
import { initAwsRum } from './utils/aws-rum'
import AppWrapper from './AppWrapper'
import ErrorMsg from './components/ErrorMsg'
import mittEmitter from './utils/mitt'
import queryClient from './queryClient'

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)

/**
 * Log version info
 */
console.log('Version: ', require('../package.json').version)

/**
 * Initialize AWS RUM web client
 */
initAwsRum()

/**
 * Publish textcut event
 * @param args
 */
const publishCutEvent =
  (cutType: Cut) => (args: Office.AddinCommands.Event) => {
    try {
      mittEmitter.emit('CutType', cutType)
    } catch (err) {
      console.error(err)
    } finally {
      // indicate office that the action has completed
      args.completed()
    }
  }

const publishNavigationEvent =
  (url: string) => (args: Office.AddinCommands.Event) => {
    try {
      mittEmitter.emit('Navigation', url)
    } catch (err) {
      console.error(err)
    } finally {
      // indicate office that the action has completed
      args.completed()
    }
  }

const publishRefDeletionEvent = async (args: Office.AddinCommands.Event) => {
  try {
    const [sheetId, rangeAddress] = await Excel.run(async (ctx) => {
      const sheet = ctx.workbook.worksheets.getActiveWorksheet()
      const range = ctx.workbook.getSelectedRange()
      sheet.load('id')
      range.load('address')
      await ctx.sync()
      const [, addr] = range.address.split('!')
      return [sheet.id, addr]
    })

    mittEmitter.emit('ReferenceDeletion', {
      sheetId,
      rangeAddress,
    })
  } catch (err) {
    console.error(err)
  } finally {
    args.completed()
  }
}

const publishOpenFeedbackEvent = async (args: Office.AddinCommands.Event) => {
  try {
    const token = await getOfficeRuntimeAccessToken().catch((err) => {
      console.log('Open support page error:', err)
      return null
    })
    let queryString = ''
    if (token) {
      queryString = `?email=${token.preferred_username}&name=${token.name}`
    }
    window.open(`/support.html${queryString}`, '_blank', 'width=820,height=820')
  } catch (err) {
    console.error(err)
  } finally {
    args.completed()
  }
}

const publishSimpleEvent =
  (func: () => void) => (args: Office.AddinCommands.Event) => {
    try {
      func()
    } catch (err) {
      console.error(err)
    } finally {
      args.completed()
    }
  }

const openUserGuide = (args: Office.AddinCommands.Event) => {
  try {
    window.open(
      'https://caseware-ap.filecloudonline.com/url/Extractly_Userguide'
    )
  } catch (err) {
    console.error(err)
  } finally {
    args.completed()
  }
}

const registerExcelWorkbookSelectionChangeEvent = () =>
  Excel.run(async (ctx) => {
    const sheets = ctx.workbook.worksheets
    sheets.onSelectionChanged.add(async (event) => {
      mittEmitter.emit('OnExcelWorkbooksSelectionChange', JSON.stringify(event))
    })
    console.log('Registering the worksheet onSelectionChange handler...')
    await ctx.sync()
  })

// render react compoent when office is ready
Office.onReady(async (info) => {
  try {
    Office.actions.associate('extractText', publishCutEvent(Cut.TEXTCUT))
    Office.actions.associate('extractTable', publishCutEvent(Cut.TABLECUT))
    Office.actions.associate('sum', publishCutEvent(Cut.SUM))
    Office.actions.associate('Redact', publishCutEvent(Cut.REDACT))
    Office.actions.associate(
      'dataMatching',
      publishNavigationEvent('/data-match')
    )
    Office.actions.associate('FilesPage', publishNavigationEvent('/files'))
    Office.actions.associate('deleteReference', publishRefDeletionEvent)
    Office.actions.associate('openFeedback', publishOpenFeedbackEvent)
    Office.actions.associate(
      'deleteAllReferences',
      publishSimpleEvent(() => mittEmitter.emit('DELETE_ALL_REFERENCES', null))
    )
    Office.actions.associate(
      'Undo',
      publishSimpleEvent(() => mittEmitter.emit('UNDO', null))
    )
    Office.actions.associate(
      'Redo',
      publishSimpleEvent(() => mittEmitter.emit('REDO', null))
    )
    Office.actions.associate('userGuide', openUserGuide)
    Office.actions.associate('validate', publishCutEvent(Cut.VALIDATE))
    Office.actions.associate('exception', publishCutEvent(Cut.EXCEPTION))
    Office.actions.associate(
      'formExtraction',
      publishSimpleEvent(() => mittEmitter.emit('OPEN_DIALOG', null))
    )
    Office.actions.associate(
      'OpenSettings',
      publishSimpleEvent(() => mittEmitter.emit('OPEN_USER_SETTINGS', null))
    )

    await registerExcelWorkbookSelectionChangeEvent()

    root.render(
      <React.StrictMode>
        {info.host === Office.HostType.Excel ? (
          <Provider store={store}>
            <QueryClientProvider client={queryClient}>
              <FormModalProvider>
                <AppWrapper />
              </FormModalProvider>
            </QueryClientProvider>
          </Provider>
        ) : (
          <div className="w-full h-full flex justify-center items-center ">
            <ErrorMsg
              title="Unsupported Host"
              content="Application must be run inside Excel environment"
            />
          </div>
        )}
      </React.StrictMode>
    )
  } catch (err) {
    console.error(err)
    const msg = err instanceof Error ? err.message : JSON.stringify(err)
    root.render(
      <React.StrictMode>
        <div className="w-full h-full flex justify-center items-center p-16 ">
          <ErrorMsg
            title="Office onReady Error"
            content={msg}
            reloadWindow={true}
          />
        </div>
      </React.StrictMode>
    )
  }
}).catch((err) => console.error('Office.onReady error:', err))
